<template>
  <div>
    <div class="text-h6 text--text mb-4">
      {{ t('Специальность', options) }}
    </div>
    <v-radio-group
      ref="doctorSpecialitiesRef"
      :value="doctorDataStore.radioGroupValue"
      class="doctor-specialities"
      data-qa="doctor_specialities"
      hide-details
      @change="doctorDataStore.setStateSelectedDoctorSpecialityId"
    >
      <v-expand-transition>
        <BaseAlert
          v-if="doctorDataStore.isErrorStateSpeciality && !doctorDataStore.radioGroupValue"
          color="text-error"
          class="mb-5"
          data-qa="doctor_specialities_alert"
          :text="t('Выберите специальность', options)"
        />
      </v-expand-transition>
      <label
        v-for="(workplace, index) in formattedWorkplaceList"
        :key="workplace.speciality.id"
        data-qa="doctor_speciaility"
        class="doctor-specialities__speciality"
        :class="{ 'mt-5': index !== 0 }"
      >
        <v-radio
          :ripple="false"
          :disabled="workplace.isDisabledWorkplace"
          :value="workplace.speciality.id"
          color="primary"
        >
          <template #label>
            <div
              class="text-body-1 text--text mr-auto"
              :class="{
                'bg-gray-80--text': workplace.isDisabledWorkplace,
                'error--text ': !workplace.isDisabledWorkplace
                  && !doctorDataStore.radioGroupValue
                  && doctorDataStore.isErrorStateSpeciality,
              }"
            >
              <span data-qa="doctor_speciaility_name">
                {{ workplace.speciality.name }}
              </span>
              <span
                v-if="workplace.specialityAges"
                data-qa="doctor_speciaility_ages"
              >
                {{ workplace.specialityAges }}
              </span>
            </div>
          </template>
        </v-radio>
      </label>
    </v-radio-group>
  </div>
</template>

<script lang="ts" setup>
import {
  onMounted, ref, watch,
} from 'vue'
import I18NextVue from 'i18next'
import { setQueryParam } from 'www/utils'
import BaseAlert from 'www/vue_components/common/core/BaseAlert.vue'
import { getSpecialityAgesText, getHasSuitableAgeInWorkplace } from 'www/vue_components/common/AppointmentPage/functions'
import {
  useDoctorDataStore,
  useAppointmentDataStore,
} from 'www/vue_components/common/AppointmentPage/stores'
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
import type { DoctorWorkplace } from 'www/vue_components/common/AppointmentPage/interfaces'

const { t, language } = I18NextVue
const options = { ns: language }

const formattedWorkplaceList = ref < Array < DoctorWorkplace & {
  isDisabledWorkplace: boolean
  specialityAges: string
}>>([])

const familyProfilesStore = useFamilyProfilesStore()
const appointmentDataStore = useAppointmentDataStore()
const doctorDataStore = useDoctorDataStore()

function getClubOrDefaultWorkplaceId() {
  if (appointmentDataStore.initAdditionalData?.specialityId) {
    return appointmentDataStore.initAdditionalData.specialityId
  }

  if (appointmentDataStore.appointmentPlaceTypes.isClub) {
    const clubWorkplace = doctorDataStore.doctorData.workplaces.find(({ isInClub }: DoctorWorkplace) => isInClub)

    if (clubWorkplace) {
      return clubWorkplace?.speciality?.id
    }
  }

  return doctorDataStore.doctorData.workplaces?.[0]?.speciality.id
}

function formatWorkplaceList() {
  const { ageNumber: ageNumberPatient } = familyProfilesStore.selectedPatient

  formattedWorkplaceList.value = doctorDataStore.doctorData.workplaces.map((workplace: DoctorWorkplace) => ({
    ...workplace,
    isDisabledWorkplace: !getHasSuitableAgeInWorkplace({
      workplace,
      ageNumberPatient,
    }),
    specialityAges: getSpecialityAgesText(workplace.age),
  }))
}

watch(() => familyProfilesStore.selectedPatient, formatWorkplaceList)

const doctorSpecialitiesRef = ref()

if (!doctorDataStore.selectedDoctorSpecialityId) {
  const specId = getClubOrDefaultWorkplaceId()

  doctorDataStore.updateStateSelectedDoctorSpecialityId(specId)

  setQueryParam('speciality', specId)
}

onMounted(() => {
  doctorDataStore.updateAppointmentSpecialitiesRef(doctorSpecialitiesRef.value.$el)

  if (familyProfilesStore.isSelectedPatient) {
    formatWorkplaceList()
  }
})

defineExpose({
  getClubOrDefaultWorkplaceId,
})
</script>

<style lang="scss" scoped>
.doctor-specialities {
  &__speciality {
    cursor: pointer;

    &:only-child::v-deep {
      .v-input--selection-controls__input {
        display: none;
      }

      .v-label {
        cursor: default;
      }
    }
  }
}
</style>
